import type { ReactNode } from 'react';
import styles from './PasswordRequirements.module.css';

export interface PasswordRule {
  /** Stable id (used as React key + for testing). */
  id: string;
  /** Rule label rendered next to the indicator. */
  label: ReactNode;
  /** Pure check function evaluated against the current password value. */
  test: (value: string) => boolean;
}

export interface PasswordRequirementsProps {
  /** The current password value to evaluate rules against. */
  value: string;
  /** Rules to render. Default: the OWASP-aligned baseline (12+, upper+lower, number, special). */
  rules?: PasswordRule[];
  /** When true, render the breached-password rule as a passing state until the server says otherwise. Default true. */
  optimisticBreached?: boolean;
  /** If the server reports the password is breached, set this to true to flag that specific rule. */
  breachedFailed?: boolean;
  /** Section heading (mono small-caps). Default: "Password requirements". */
  heading?: ReactNode;
}

export const DEFAULT_PASSWORD_RULES: PasswordRule[] = [
  { id: 'length', label: 'At least 12 characters', test: (v) => v.length >= 12 },
  {
    id: 'case',
    label: 'Upper and lower case',
    test: (v) => /[A-Z]/.test(v) && /[a-z]/.test(v),
  },
  { id: 'number', label: 'One number', test: (v) => /\d/.test(v) },
  { id: 'special', label: 'One special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const BREACHED_RULE_ID = 'breached';

/**
 * Live checklist of password rules. Each rule renders a circle indicator
 * that flips to a filled check when its test passes.
 *
 * The breached-password rule is rendered optimistically green (the server is
 * the source of truth via k-anonymity range query) — set `breachedFailed`
 * to true after a server response to flag it.
 */
export function PasswordRequirements({
  value,
  rules = DEFAULT_PASSWORD_RULES,
  optimisticBreached = true,
  breachedFailed = false,
  heading = 'Password requirements',
}: PasswordRequirementsProps) {
  const fullList: Array<{ id: string; label: ReactNode; passed: boolean }> = rules.map((rule) => ({
    id: rule.id,
    label: rule.label,
    passed: rule.test(value),
  }));

  // Append the breached-password rule (special-cased — not a pure client test).
  fullList.push({
    id: BREACHED_RULE_ID,
    label: 'Not found in known breaches',
    // optimistic: render as passed unless the server has come back with a fail.
    passed: value.length > 0 && optimisticBreached && !breachedFailed,
  });

  return (
    <div className={styles.wrapper} aria-live="polite">
      {heading && <p className={styles.heading}>{heading}</p>}
      <ul className={styles.list}>
        {fullList.map((item) => (
          <li
            key={item.id}
            className={[styles.item, item.passed ? styles.itemPassed : null]
              .filter(Boolean)
              .join(' ')}
          >
            <span className={styles.indicator} aria-hidden="true">
              {item.passed ? <CheckGlyph /> : <DotGlyph />}
            </span>
            <span className={styles.label}>{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CheckGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6.2 L5 8.5 L9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DotGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <circle cx="6" cy="6" r="2" fill="currentColor" />
    </svg>
  );
}
